import PropTypes from 'prop-types';
import { memo, useEffect, useMemo, useState } from 'react';
import { queryCache, useMutation, useQuery } from 'react-query';

import Button from 'shopper/components/Button';

import { sendEvent } from 'lib/gtag';
import { userQueryKeys } from 'lib/queryKeys';
import { del, get, post } from 'lib/request';

import { useUser } from 'providers/UserProvider';

const postFollowUser = (userId) => post(`/user/${userId}/follow`);
const deleteUnfollowUser = (userId) => del(`/user/${userId}/follow`);

const isFollowingUser = (followedUsers, userId) =>
  followedUsers?.length > 0 &&
  followedUsers.some(({ userId: followedUserId }) => followedUserId === userId);

const FollowUserButton = ({ gaEventCategory, userId, ...rest }) => {
  const {
    isLoggedIn,
    LoggedInUser,
    loadingLoggedInUser,
    runCallbackIfLoggedIn,
  } = useUser();
  const [buttonLabel, setButtonLabel] = useState('Seguir');
  const { data: followedUsers } = useQuery(
    userQueryKeys.followersList({ userId: LoggedInUser?.userId }),
    {
      queryFn: () => get('/user/follows'),
      staleTime: Infinity,
      enabled: isLoggedIn,
      onSuccess: (followedUsersRes) => {
        if (isFollowingUser(followedUsersRes, userId)) {
          setButtonLabel('Seguindo');
          return;
        }

        setButtonLabel('Seguir');
      },
    }
  );

  const [mutateFollow] = useMutation(postFollowUser, {
    onMutate: () => setButtonLabel('Seguindo'),
    onSuccess: ({ followedUser }) => {
      queryCache.setQueryData(
        userQueryKeys.followersList({ userId: LoggedInUser?.userId }),
        [...followedUsers, followedUser]
      );
    },
  });

  const [mutateUnfollow] = useMutation(deleteUnfollowUser, {
    onMutate: () => setButtonLabel('Seguir'),
    onSuccess: (_, user) => {
      queryCache.setQueryData(
        userQueryKeys.followersList({ userId: LoggedInUser?.userId }),
        followedUsers.filter(
          ({ userId: followedUserId }) => followedUserId !== user
        )
      );
    },
  });

  const isFollowingUserMemoized = useMemo(
    () => isFollowingUser(followedUsers, userId),
    [followedUsers, userId]
  );

  const onClickHandler = () => {
    if (isFollowingUserMemoized) {
      sendEvent({ action: 'button_unfollow', category: gaEventCategory });
      runCallbackIfLoggedIn(mutateUnfollow, userId);
      return;
    }

    sendEvent({
      action: 'button_follow',
      category: gaEventCategory,
    });
    runCallbackIfLoggedIn(mutateFollow, userId);
  };

  const onMouseEnter = () => {
    if (isFollowingUserMemoized) {
      setButtonLabel('Deixar de seguir');
    }
  };

  const onMouseLeave = () => {
    if (isFollowingUserMemoized) {
      setButtonLabel('Seguindo');
    }
  };

  useEffect(() => {
    setButtonLabel(isFollowingUserMemoized ? 'Seguindo' : 'Seguir');
  }, [userId]);

  if (
    loadingLoggedInUser ||
    (!loadingLoggedInUser && !LoggedInUser?.canFollowUser(userId))
  ) {
    return null;
  }

  return (
    <Button
      type={isFollowingUserMemoized ? 'primary' : 'secondary'}
      size="size3"
      width={['100%', null, 'unset']}
      onClick={onClickHandler}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...rest}
    >
      {buttonLabel}
    </Button>
  );
};

FollowUserButton.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  userId: PropTypes.number.isRequired,
};

export default memo(FollowUserButton);
